'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type Role = 'admin' | 'manager' | 'contributor' | 'analyst' | null;

interface RoleContextType {
  role: Role;
  loading: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isContributor: boolean;
  isAnalyst: boolean;
  can: (action: string) => boolean;
}

const RoleContext = createContext<RoleContextType>({
  role: null,
  loading: true,
  isAdmin: false,
  isManager: false,
  isContributor: false,
  isAnalyst: false,
  can: () => false,
});

export const useRole = () => useContext(RoleContext);

const PERMISSIONS: Record<string, Role[]> = {
  'campaigns:create': ['admin', 'manager', 'contributor'],
  'campaigns:delete': ['admin', 'manager'],
  'campaigns:launch': ['admin', 'manager'],
  'contacts:import': ['admin', 'manager', 'contributor'],
  'contacts:delete': ['admin', 'manager'],
  'segments:create': ['admin', 'manager', 'contributor'],
  'segments:delete': ['admin', 'manager'],
  'pages:publish': ['admin', 'manager'],
  'pages:delete': ['admin', 'manager'],
  'settings:edit': ['admin'],
  'organizations:manage': ['admin'],
  'analytics:export': ['admin', 'manager', 'analyst'],
  'rewards:manage': ['admin', 'manager'],
  'newsletter:send': ['admin', 'manager'],
  'invites:send': ['admin', 'manager'],
};

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const supabase = createClient();
  const [role, setRole] = useState<Role>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setRole(null);
      setLoading(false);
      return;
    }
    const fetchRole = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (error || !data?.role) {
          // New user with no profile yet — default to contributor (not admin)
          setRole('contributor');
        } else {
          setRole(data.role as Role);
        }
      } catch {
        // On unexpected error, default to contributor (not admin)
        setRole('contributor');
      } finally {
        setLoading(false);
      }
    };
    fetchRole();
  }, [user]);

  const can = (action: string): boolean => {
    if (!role) return false;
    const allowed = PERMISSIONS[action];
    if (!allowed) return role === 'admin';
    return allowed.includes(role);
  };

  return (
    <RoleContext.Provider value={{
      role,
      loading,
      isAdmin: role === 'admin',
      isManager: role === 'manager' || role === 'admin',
      isContributor: role === 'contributor' || role === 'manager' || role === 'admin',
      isAnalyst: role === 'analyst' || role === 'admin',
      can,
    }}>
      {children}
    </RoleContext.Provider>
  );
}
